/**
 * Prompts y contrato JSON según enfoque didáctico (teórico / práctico / indagación).
 * Consumir antes de llamar al endpoint de generación con IA.
 */

import { isFormulaAllowedSubject } from '../../activities/guide_editorial/guide_editorial.logic';

export type EnfoqueDidactico = 'teorico' | 'practico' | 'aprendizaje' | 'indagacion';

/** Alias usados en el modal del Studio (ExamFocusId). */
export type ExamFocusLegacyId = 'conceptual' | 'learning' | 'practical' | 'inquiry';

export const ENFOQUE_DIDACTICO_VALUES: readonly EnfoqueDidactico[] = [
  'teorico',
  'practico',
  'aprendizaje',
  'indagacion',
] as const;

/** Tipos pedagógicos que la IA devuelve en blocks[].type — el frontend los interpreta antes del runtime Edusico. */
export type AiPedagogicalBlockType =
  | 'content_reading'
  | 'definition_glossary'
  | 'atlas_context'
  | 'section_header'
  | 'highlight_box'
  | 'image_placeholder'
  | 'glossary_table'
  | 'math_formula'
  | 'quiz_multiple'
  | 'quiz_true_false'
  | 'fill_blank'
  | 'matching_pairs'
  | 'ordering_steps'
  | 'open_case'
  | 'inquiry_scenario'
  | 'research_prompt'
  | 'classification_groups';

export interface AiQuizOption {
  id: string;
  text: string;
  isCorrect?: boolean;
}

export interface AiGlossaryEntry {
  term: string;
  definition: string;
}

export interface AiMatchingPair {
  id: string;
  left: string;
  right: string;
}

export interface AiClassificationGroup {
  id: string;
  label: string;
  itemIds: string[];
}

export interface AiClassificationItem {
  id: string;
  text: string;
}

/** Bloque genérico devuelto por la IA — campos opcionales según type. */
export interface AiGeneratedBlock {
  type: AiPedagogicalBlockType;
  title: string;
  /** Texto principal: lectura, consigna, escenario, etc. */
  body?: string;
  /** Pistas o andamiaje (indagación). */
  hints?: string[];
  /** Solo content_reading / atlas_context */
  sections?: Array<{ heading?: string; text: string }>;
  /** definition_glossary */
  glossary?: AiGlossaryEntry[];
  /** quiz_multiple | quiz_true_false */
  question?: string;
  options?: AiQuizOption[];
  correctIndex?: number;
  /** fill_blank */
  textWithBlanks?: string;
  answers?: string[];
  /** matching_pairs */
  pairs?: AiMatchingPair[];
  /** ordering_steps */
  steps?: string[];
  correctOrder?: number[];
  /** open_case | inquiry_scenario | research_prompt */
  prompt?: string;
  rubric?: string[];
  /** classification_groups */
  categories?: AiClassificationGroup[];
  items?: AiClassificationItem[];
  /** image_placeholder */
  alt_text?: string;
  altText?: string;
  caption?: string;
  /** highlight_box */
  tone?: 'mint' | 'lavender' | 'amber';
  /** math_formula — LaTeX puro sin delimitadores $ */
  latex?: string;
  formula?: string;
  description?: string;
  /** Puente opcional hacia modos Edusico Sala (arena, completar, …) */
  edusicoMode?: string;
  /** Instrucción visual en inglés para fal.ai (máx. ~15 palabras). */
  imagePrompt?: string;
  /** Alias que a veces devuelve Mistral en lugar de title */
  heading?: string;
}

const BLOCK_TITLE_FALLBACK: Partial<Record<AiPedagogicalBlockType, string>> = {
  section_header: 'Sección',
  highlight_box: 'Destacado',
  image_placeholder: 'Recurso visual',
  glossary_table: 'Glosario',
  math_formula: 'Fórmula',
  content_reading: 'Lectura',
  definition_glossary: 'Definiciones',
  atlas_context: 'Contexto',
  quiz_multiple: 'Pregunta',
  quiz_true_false: 'Verdadero o falso',
  fill_blank: 'Completar',
  matching_pairs: 'Emparejar',
  ordering_steps: 'Ordenar',
  open_case: 'Caso abierto',
  inquiry_scenario: 'Escenario',
  research_prompt: 'Investigación',
  classification_groups: 'Clasificar',
};

/** Deriva title cuando Mistral omite el campo o usa heading/body. */
export function normalizeAiGeneratedBlock(
  block: AiGeneratedBlock,
  index = 0,
): AiGeneratedBlock {
  const raw = block as AiGeneratedBlock & Record<string, unknown>;
  const candidates = [
    raw.title,
    raw.heading,
    raw.prompt,
    raw.question,
    raw.body,
    raw.caption,
    raw.alt_text,
    raw.altText,
    raw.latex,
    raw.formula,
    raw.description,
    raw.sections?.[0]?.heading,
    raw.sections?.[0]?.text,
    raw.glossary?.[0]?.term,
  ]
    .map((v) => String(v || '').trim())
    .filter(Boolean);

  const title =
    candidates[0]?.slice(0, 120) ||
    `${BLOCK_TITLE_FALLBACK[block.type] ?? 'Bloque'} ${index + 1}`;

  return { ...block, title };
}

export interface AiExamGenerationResponse {
  title: string;
  topic: string;
  enfoqueDidactico: EnfoqueDidactico;
  levelHint?: string;
  blocks: AiGeneratedBlock[];
}

const ANTI_PROMPT_ECHO_RULE =
  'CRÍTICO: El JSON anterior es SOLO una plantilla estructural. TIENES ESTRICTAMENTE PROHIBIDO copiar los textos, conceptos o temas del ejemplo en tu respuesta final. Tu contenido debe basarse ÚNICAMENTE en el tema solicitado por el usuario.';

const TEORICO_EDITORIAL_RULES = `
REGLAS DE ESTRUCTURA (INNEGOCIABLES):

MANDATORY STRUCTURE: Tu respuesta JSON DEBE seguir este orden lógico secuencial en el array "blocks" (respeta el índice 0, 1, 2…):
1. "section_header" (Introducción)
2. "content_reading" (Concepto base — máximo 2-3 oraciones)
3. "math_formula" (La ecuación principal, si aplica al tema)
4. "section_header" (Ejemplo o Pasos)
5. "content_reading" o "highlight_box" (Desarrollo del ejemplo)
6. "definition_glossary" o "glossary_table" (Resumen de términos al final)

No agrupes títulos. Cada "section_header" DEBE estar inmediatamente seguido por su contenido en el array.
Si generas más bloques, intercálalos siguiendo el patrón título → contenido. Nunca pongas todas las fórmulas al final ni todos los títulos al inicio.

PÁRRAFOS CORTOS: "content_reading" extremadamente conciso (máximo 2 o 3 oraciones). Si hay pasos, sepáralos en bloques distintos.

LISTAS Y PASOS: Si enumeras pasos o ítems dentro de "content_reading", DEBES separar cada ítem con saltos de línea dobles (\\n\\n). Nunca concatenes "1. Paso uno. 2. Paso dos." en una sola línea.

FÓRMULAS: NUNCA incluyas ecuaciones dentro de "content_reading". Usa "math_formula" con LaTeX puro (ej: \\Delta\\% = \\frac{V_f - V_i}{V_i} \\times 100) y campo "description" para las variables.

IMÁGENES: "image_placeholder" máximo 1 por guía, solo si es absolutamente crítico.`;

const TEORICO_HUMANITIES_EDITORIAL_RULES = `
REGLAS DE ESTRUCTURA (INNEGOCIABLES):

MANDATORY STRUCTURE: Tu respuesta JSON DEBE seguir este orden lógico secuencial en el array "blocks" (respeta el índice 0, 1, 2…):
1. "section_header" (Introducción)
2. "content_reading" (Concepto base — máximo 2-3 oraciones, viñetas y **negritas**)
3. "highlight_box" (Idea o dato clave del tema)
4. "section_header" (Ejemplo o desarrollo)
5. "content_reading" o "highlight_box" (Desarrollo del ejemplo)
6. "definition_glossary" o "glossary_table" (Resumen de términos al final)

No agrupes títulos. Cada "section_header" DEBE estar inmediatamente seguido por su contenido en el array.
Si generas más bloques, intercálalos siguiendo el patrón título → contenido. Nunca pongas todos los títulos al inicio.

PÁRRAFOS CORTOS: "content_reading" extremadamente conciso (máximo 2 o 3 oraciones). Si hay pasos, sepáralos en bloques distintos.

LISTAS Y PASOS: Si enumeras pasos o ítems dentro de "content_reading", DEBES separar cada ítem con saltos de línea dobles (\\n\\n). Nunca concatenes "1. Paso uno. 2. Paso dos." en una sola línea.

PROHIBIDO: math_formula, LaTeX, ecuaciones algebraicas, formula_destacada y cualquier notación matemática. Usa highlight_box o content_reading con viñetas.

IMÁGENES: "image_placeholder" cada 2-3 bloques de lectura en guías largas; alt_text concreto y caption pedagógico.`;

const ENFOQUE_ROLE_INSTRUCTIONS: Record<EnfoqueDidactico, string> = {
  teorico:
    'Actúa como un diseñador editorial de apuntes escolares (estilo atlas limpio). Tu objetivo es PRESENTAR y EXPLICAR la materia con bloques de lectura ricos: textos breves, subtítulos de sección, fórmulas destacadas en LaTeX, cajas destacadas y glosarios en tabla. BAJO NINGUNA CIRCUNSTANCIA generes preguntas, ejercicios, cuestionarios, completar espacios, vinculaciones, ordenamientos ni evaluación. Solo contenido de lectura y estudio.',
  practico:
    'Actúa como un evaluador. Tu objetivo es que el estudiante APLIQUE y MECANICE lo aprendido. Genera exclusivamente una batería de ejercicios y preguntas de evaluación directa sobre el tema (selección múltiple, completar espacios, verdadero/falso).',
  aprendizaje:
    'Actúa como un docente que diseña una GUÍA DE APRENDIZAJE. Primero ENSEÑA el concepto con prosa clara (definición + ejemplo modelado; sin ganchos «Imagina que…» si el slot es factual). Después da PRÁCTICA guiada y luego independiente alineada a lo enseñado. No seas un evaluador puro: el andamiaje teórico obligatorio precede a los ejercicios. Prohibido saltar directo a una batería de ítems sin explicación.',
  indagacion:
    'Actúa como un facilitador. Tu objetivo es DESPERTAR LA CURIOSIDAD y el análisis crítico. Plantea escenarios, problemas abiertos o preguntas de investigación donde el estudiante reciba pistas y deba deducir, explorar o argumentar la respuesta.',
};

const ALLOWED_TYPES_BY_ENFOQUE: Record<EnfoqueDidactico, readonly AiPedagogicalBlockType[]> = {
  teorico: [
    'content_reading',
    'section_header',
    'math_formula',
    'highlight_box',
    'image_placeholder',
    'glossary_table',
    'definition_glossary',
    'atlas_context',
  ],
  practico: [
    'quiz_multiple',
    'quiz_true_false',
    'fill_blank',
    'matching_pairs',
    'ordering_steps',
    'classification_groups',
  ],
  aprendizaje: [
    'content_reading',
    'section_header',
    'math_formula',
    'highlight_box',
    'glossary_table',
    'definition_glossary',
    'quiz_multiple',
    'fill_blank',
    'matching_pairs',
    'ordering_steps',
    'classification_groups',
    'inquiry_scenario',
  ],
  indagacion: ['inquiry_scenario', 'open_case', 'research_prompt', 'fill_blank'],
};

/** Unión de todos los types pedagógicos (no confundir con modos Edusico como info_block, arena…). */
export const ALL_AI_PEDAGOGICAL_BLOCK_TYPES = [
  ...new Set([
    ...ALLOWED_TYPES_BY_ENFOQUE.teorico,
    ...ALLOWED_TYPES_BY_ENFOQUE.practico,
    ...ALLOWED_TYPES_BY_ENFOQUE.aprendizaje,
    ...ALLOWED_TYPES_BY_ENFOQUE.indagacion,
  ]),
] as AiPedagogicalBlockType[];

const PEDAGOGICAL_TYPE_SET = new Set<string>(ALL_AI_PEDAGOGICAL_BLOCK_TYPES);

export function isAiPedagogicalBlockType(type: unknown): type is AiPedagogicalBlockType {
  return PEDAGOGICAL_TYPE_SET.has(String(type || '').trim());
}

const FOCUS_TO_ENFOQUE: Record<ExamFocusLegacyId, EnfoqueDidactico> = {
  conceptual: 'teorico',
  learning: 'aprendizaje',
  practical: 'practico',
  // UI «Cuestionario» = práctica simulada (formativa), no indagación ni evaluación.
  inquiry: 'practico',
};

const ENFOQUE_TO_FOCUS: Record<EnfoqueDidactico, ExamFocusLegacyId> = {
  teorico: 'conceptual',
  aprendizaje: 'learning',
  practico: 'practical',
  indagacion: 'inquiry',
};

export function normalizeEnfoqueDidactico(
  value: string | null | undefined,
  fallback: EnfoqueDidactico = 'practico',
): EnfoqueDidactico {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (raw === 'teorico' || raw === 'theoretical' || raw === 'conceptual') return 'teorico';
  if (raw === 'aprendizaje' || raw === 'learning' || raw === 'guia' || raw === 'guide') return 'aprendizaje';
  if (raw === 'practico' || raw === 'practical' || raw === 'practice') return 'practico';
  if (raw === 'indagacion' || raw === 'inquiry' || raw === 'investigation') return 'indagacion';
  if (ENFOQUE_DIDACTICO_VALUES.includes(raw as EnfoqueDidactico)) return raw as EnfoqueDidactico;
  return fallback;
}

export function enfoqueFromExamFocus(focus: string | undefined): EnfoqueDidactico {
  const key = String(focus || '').trim() as ExamFocusLegacyId;
  return FOCUS_TO_ENFOQUE[key] ?? 'practico';
}

export function examFocusFromEnfoque(enfoque: EnfoqueDidactico): ExamFocusLegacyId {
  return ENFOQUE_TO_FOCUS[enfoque];
}

export function getAllowedBlockTypes(
  enfoque: EnfoqueDidactico,
  subjectTag?: string,
): readonly AiPedagogicalBlockType[] {
  const base = ALLOWED_TYPES_BY_ENFOQUE[enfoque];
  if (enfoque === 'teorico' && !isFormulaAllowedSubject(subjectTag)) {
    return base.filter((type) => type !== 'math_formula');
  }
  if (enfoque === 'aprendizaje' && !isFormulaAllowedSubject(subjectTag)) {
    return base.filter((type) => type !== 'math_formula');
  }
  return base;
}

function getTeoricoRoleInstruction(subjectTag?: string): string {
  if (!isFormulaAllowedSubject(subjectTag)) {
    return (
      'Actúa como un diseñador editorial de apuntes escolares (estilo atlas limpio). Tu objetivo es PRESENTAR y EXPLICAR la materia con bloques de lectura ricos: textos breves con viñetas y negritas, subtítulos de sección, cajas destacadas y glosarios en tabla. BAJO NINGUNA CIRCUNSTANCIA generes preguntas, ejercicios, cuestionarios, completar espacios, vinculaciones, ordenamientos ni evaluación. Solo contenido de lectura y estudio. PROHIBIDO math_formula, LaTeX y fórmulas algebraicas.'
    );
  }
  return ENFOQUE_ROLE_INSTRUCTIONS.teorico;
}

function getTeoricoEditorialRules(subjectTag?: string): string {
  return isFormulaAllowedSubject(subjectTag)
    ? TEORICO_EDITORIAL_RULES
    : TEORICO_HUMANITIES_EDITORIAL_RULES;
}

/** Instrucción de rol según enfoque — núcleo del system prompt. */
export function getSystemPromptByEnfoque(
  enfoque: EnfoqueDidactico,
  tema: string,
  subjectTag?: string,
): string {
  const safeTema = String(tema || 'el tema indicado').trim() || 'el tema indicado';
  const role =
    enfoque === 'teorico'
      ? getTeoricoRoleInstruction(subjectTag)
      : ENFOQUE_ROLE_INSTRUCTIONS[enfoque];
  const allowed = getAllowedBlockTypes(enfoque, subjectTag).join(', ');
  return `${role}

Tema central de esta sesión: "${safeTema}".

Tipos de bloque permitidos para este enfoque (usa SOLO estos valores en blocks[].type):
${allowed}

Prohibido mezclar tipos de otros enfoques. Todo el contenido debe ser específico del tema, en español chileno, sin placeholders genéricos.${
    enfoque === 'teorico' ? `\n\n${getTeoricoEditorialRules(subjectTag)}` : ''
  }`;
}

function jsonSchemaForEnfoque(
  enfoque: EnfoqueDidactico,
  exactBlocks: number,
  subjectTag?: string,
): string {
  const types = getAllowedBlockTypes(enfoque, subjectTag).map((t) => `"${t}"`).join(' | ');
  const mathFields = isFormulaAllowedSubject(subjectTag)
    ? `
      "latex": "string (math_formula — LaTeX puro, sin delimitadores $)",
      "description": "string (math_formula — significado de variables)",`
    : '';
  return `Responde SOLO con un objeto JSON válido (sin markdown ni texto extra):

{
  "title": "Título principal del [TEMA_SOLICITADO] (máx. 96 caracteres)",
  "topic": "[TEMA_SOLICITADO]",
  "enfoqueDidactico": "${enfoque}",
  "blocks": [
    {
      "type": ${types},
      "title": "Título del bloque sobre [TEMA_SOLICITADO]",
      "heading": "Subtítulo opcional del [TEMA_SOLICITADO]",
      "body": "Párrafo explicativo introduciendo el [TEMA_SOLICITADO]. Aquí debes desarrollar la idea central sin usar ejemplos de otras materias.",
      "sections": [{ "heading": "Sección del [TEMA_SOLICITADO]", "text": "Texto de la sección" }],
      "glossary": [{ "term": "Término del [TEMA_SOLICITADO]", "definition": "Definición contextual" }],
      "alt_text": "Descripción visual del [TEMA_SOLICITADO]",
      "caption": "Pie de foto sobre [CONCEPTO_CLAVE]",${mathFields}
      "tone": "mint|lavender|amber (highlight_box)",
      "question": "¿Pregunta evaluativa sobre [CONCEPTO_CLAVE]?",
      "options": [{ "id": "o1", "text": "Opción sobre [CONCEPTO_CLAVE]" }],
      "correctIndex": 0,
      "textWithBlanks": "Oración sobre [TEMA_SOLICITADO] con [CONCEPTO_CLAVE] en el hueco",
      "answers": ["Respuesta deducible del contexto"],
      "pairs": [{ "id": "p1", "left": "Idea A del [TEMA_SOLICITADO]", "right": "Relación con [CONCEPTO_CLAVE]" }],
      "steps": ["Paso 1 sobre [TEMA_SOLICITADO]", "Paso 2 sobre [CONCEPTO_CLAVE]"],
      "correctOrder": [0, 1],
      "prompt": "¿Pregunta abierta sobre [CONCEPTO_CLAVE]?",
      "hints": ["Pista sobre [TEMA_SOLICITADO]"],
      "rubric": ["Criterio observable 1", "Criterio observable 2"],
      "categories": [{ "id": "c1", "label": "Categoría del [TEMA_SOLICITADO]", "itemIds": ["i1"] }],
      "items": [{ "id": "i1", "text": "Elemento de [CONCEPTO_CLAVE]" }]
    }
  ]
}

El array "blocks" debe tener EXACTAMENTE ${exactBlocks} elementos.
Incluye solo los campos relevantes para cada type; omite los demás.

${ANTI_PROMPT_ECHO_RULE}`;
}

export interface BuildAiGenerationMessagesOptions {
  enfoqueDidactico: EnfoqueDidactico | string;
  tema: string;
  exactBlocks: number;
  levelConstraints?: string;
  subjectExpertPrompt?: string;
  subjectTag?: string;
  resourceType?: 'exam' | 'guide';
  /** Texto adicional del usuario (extensión, materia, etc.) */
  userContext?: string;
}

export interface AiGenerationMessages {
  enfoque: EnfoqueDidactico;
  allowedBlockTypes: readonly AiPedagogicalBlockType[];
  systemPrompt: string;
  userPrompt: string;
  messages: Array<{ role: 'system' | 'user'; content: string }>;
}

/** Ensambla system + user listos para Mistral / OpenAI antes del fetch. */
export function buildAiGenerationMessages(options: BuildAiGenerationMessagesOptions): AiGenerationMessages {
  const enfoque = normalizeEnfoqueDidactico(options.enfoqueDidactico);
  const tema = String(options.tema || '').trim();
  const exactBlocks = Math.max(1, Number(options.exactBlocks) || 8);
  const resourceLabel = options.resourceType === 'guide' ? 'guía de aprendizaje' : 'evaluación';

  const systemParts = [
    getSystemPromptByEnfoque(enfoque, tema, options.subjectTag),
    '',
    jsonSchemaForEnfoque(enfoque, exactBlocks, options.subjectTag),
  ];

  const userParts = [
    `Genera una ${resourceLabel} sobre: ${tema}`,
    `- Cantidad exacta de bloques: ${exactBlocks}`,
  ];

  if (options.levelConstraints?.trim()) {
    userParts.push(`- Nivel escolar: ${options.levelConstraints.trim()}`);
  }
  if (options.subjectExpertPrompt?.trim()) {
    userParts.push(`- Experticia de la asignatura:\n${options.subjectExpertPrompt.trim()}`);
  }
  if (options.userContext?.trim()) {
    userParts.push(options.userContext.trim());
  }

  const systemPrompt = systemParts.join('\n');
  const userPrompt = userParts.join('\n');

  return {
    enfoque,
    allowedBlockTypes: getAllowedBlockTypes(enfoque, options.subjectTag),
    systemPrompt,
    userPrompt,
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ],
  };
}

/** Valida superficialmente la respuesta parseada de la IA. */
export function validateAiExamGenerationResponse(
  raw: unknown,
  expectedEnfoque?: EnfoqueDidactico,
): AiExamGenerationResponse {
  if (!raw || typeof raw !== 'object') throw new Error('AI response must be an object');
  const data = raw as Partial<AiExamGenerationResponse>;
  if (!data.title?.trim()) throw new Error('AI response requires title');
  if (!Array.isArray(data.blocks) || data.blocks.length < 1) {
    throw new Error('AI response requires non-empty blocks array');
  }

  const enfoque = normalizeEnfoqueDidactico(data.enfoqueDidactico, expectedEnfoque ?? 'practico');
  if (expectedEnfoque && enfoque !== expectedEnfoque) {
    throw new Error(`AI response enfoque mismatch: expected ${expectedEnfoque}, got ${enfoque}`);
  }

  const allowed = new Set(ALLOWED_TYPES_BY_ENFOQUE[enfoque]);
  // Leniente: un tipo alucinado/mal traducido por el LLM (ej. "development") no debe tirar
  // abajo TODA la generación — se descarta ese bloque puntual y se sigue con el resto,
  // igual que hace el parser leniente para el resto de las respuestas del LLM.
  const blocks: AiGeneratedBlock[] = data.blocks
    .filter((block, i) => {
      if (!block || typeof block !== 'object') {
        console.warn(`[enfoqueDidacticoPrompt] Block ${i} invalid — descartado`);
        return false;
      }
      const b = block as AiGeneratedBlock;
      if (!b.type || !allowed.has(b.type)) {
        console.warn(
          `[enfoqueDidacticoPrompt] Block ${i} type "${String(b.type)}" not allowed for enfoque ${enfoque} — descartado`,
        );
        return false;
      }
      return true;
    })
    .map((block, i) => normalizeAiGeneratedBlock(block as AiGeneratedBlock, i));

  if (blocks.length === 0) {
    throw new Error(`AI response has no valid blocks for enfoque ${enfoque}`);
  }

  return {
    title: String(data.title).trim(),
    topic: String(data.topic || data.title).trim(),
    enfoqueDidactico: enfoque,
    levelHint: data.levelHint ? String(data.levelHint) : undefined,
    blocks,
  };
}

export function isStudyGuideEnfoque(enfoque: EnfoqueDidactico | string | null | undefined): boolean {
  return normalizeEnfoqueDidactico(enfoque, 'practico') === 'teorico';
}

/** Mapa pedagógico → modo Edusico Sala (para adaptadores downstream). */
export function mapAiBlockTypeToEdusicoMode(type: AiPedagogicalBlockType): string {
  switch (type) {
    case 'content_reading':
    case 'definition_glossary':
    case 'atlas_context':
      return 'info_block';
    case 'section_header':
      return 'subtitulo';
    case 'highlight_box':
      return 'destacado';
    case 'image_placeholder':
      return 'figura';
    case 'glossary_table':
      return 'glosario_tabla';
    case 'math_formula':
      return 'formula_destacada';
    case 'quiz_multiple':
    case 'quiz_true_false':
      return 'arena';
    case 'fill_blank':
      return 'completar';
    case 'matching_pairs':
      return 'vincula';
    case 'ordering_steps':
      return 'order';
    case 'classification_groups':
      return 'agrupa';
    case 'open_case':
    case 'research_prompt':
    case 'inquiry_scenario':
      return 'desarrollo';
    default:
      return 'info_block';
  }
}
